import React, { useState, useEffect } from 'react'
import { ServerConfig } from 'config'
import axios from 'axios'
import { useSelector } from 'react-redux'
import { makeStyles } from '@material-ui/core/styles'
import { Typography, Modal, Button, Box } from '@material-ui/core'
import _ from 'lodash'

// same event NonPaginatedList listens for, so the list refreshes after a
// device is created here without needing to touch NonPaginatedList itself
const evt = new Event('customertable_is_dirty')

const useStyles = makeStyles((theme) => ({
  detailsBox: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '80%',
    backgroundColor: theme.palette.background.default,
    border: '2px solid #000',
    boxShadow: 24,
    padding: 4,
    maxHeight: '100vh',
    overflowY: 'auto'
  },
  fieldLabel: {
    display: 'block',
    width: '150px',
  },
  formGroup: {
    float: 'left',
    margin: '10px',
  },
  formControl: {
    display: 'block',
    width: '256px',
  },
  detailsStatus: {
    lineHeight: '36px',
    padding: '10px'
  },
  errorMessage: {
    color: 'red'
  },
  submitButton: {
    margin: '10px',
    display: 'block',
    float: 'left'
  },
  breakFloat: {
    clear: 'both'
  },
  secretBox: {
    clear: 'both',
    margin: '10px',
    padding: '10px',
    border: '2px solid #d32f2f',
  },
  secretWarning: {
    color: '#d32f2f',
    fontWeight: 'bold',
  },
  secretValue: {
    fontFamily: 'monospace',
    fontSize: '16px',
    wordBreak: 'break-all',
    userSelect: 'all',
    padding: '8px',
    display: 'block',
    backgroundColor: theme.palette.background.paper,
    marginTop: '8px',
    marginBottom: '8px',
  },
}))

// The generated client_secret can only ever be read back here, right after
// creation - the backend stores only its bcrypt hash. There is no "view
// secret" anywhere else, by design.
const CreatedSecretBox = ({ device, clientSecret, classes }) => {
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    navigator.clipboard?.writeText(clientSecret)
    setCopied(true)
  }

  return (
    <Box className={classes.secretBox}>
      <Typography className={classes.secretWarning}>
        ATM device "{device.name}" created. Copy this secret now - it will
        never be shown again. If it's lost, use "regenerate secret" via the
        API instead (there is no way to recover this value).
      </Typography>
      <Typography variant="body2">client_id: <b>{device.id}</b></Typography>
      <code className={classes.secretValue}>{clientSecret}</code>
      <Button onClick={handleCopy}>{copied ? 'Copied!' : 'Copy secret'}</Button>
    </Box>
  )
}

const AtmDeviceAddForm = (props) => {
  const [tenants, setTenants] = useState([])
  const [tenantsites, setTenantsites] = useState([])
  const [status, setStatus] = useState({ kind: 'idle', message: '' })
  const [created, setCreated] = useState(null)  // { device, client_secret }

  const classes = useStyles()
  const auth = useSelector(state => state.auth)
  const add_new_url = props.add_new_url

  useEffect(() => {
    const config = { headers: { 'Content-Type': 'application/json' } }
    if (auth.access_token) {
      config.headers.Authorization = `Bearer ${auth.access_token}`
    }

    axios.get(`${ServerConfig.SERVER_URL}/admin/tenants`, config)
      .then(results => setTenants(results.data))

    axios.get(`${ServerConfig.SERVER_URL}/admin/tenant_sites`, config)
      .then(results => setTenantsites(results.data))
  }, [])

  const handleSubmit = async (event) => {
    event.preventDefault()

    const config = { headers: { 'Content-Type': 'application/json' } }
    if (auth.access_token) {
      config.headers.Authorization = `Bearer ${auth.access_token}`
    }

    const data = new FormData(event.target)
    const value = Object.fromEntries(data.entries())

    setStatus({ kind: 'loading', message: 'Creating...' })

    await axios.post(add_new_url, value, config)
      .then(response => {
        setStatus({ kind: 'success', message: 'Created' })
        setCreated({ device: response.data, client_secret: response.data.client_secret })
        window.dispatchEvent(evt)
      })
      .catch(error => {
        const msg = error.response?.data?.message || error.response?.data?.msg
        setStatus({ kind: 'error', message: msg || 'Error creating ATM device' })
      })
  }

  if (created) {
    return <CreatedSecretBox device={created.device} clientSecret={created.client_secret} classes={classes} />
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className={`form-group ${classes.formGroup}`}>
        <label htmlFor="name" className={classes.fieldLabel}>Name</label>
        <input type="text" className="form-control" name="name" id="name" />
      </div>

      <div className={`form-group ${classes.formGroup}`}>
        <label htmlFor="tenant_id" className={classes.fieldLabel}>Tenant</label>
        <select className={`form-control ${classes.formControl}`} name="tenant_id" id="tenant_id">
          <option value="">Please Select</option>
          {_.map(tenants, opt => (
            <option key={opt.id} value={opt.id}>{opt.business_name}</option>
          ))}
        </select>
      </div>

      <div className={`form-group ${classes.formGroup}`}>
        <label htmlFor="tenant_site_id" className={classes.fieldLabel}>Tenant Site</label>
        <select className={`form-control ${classes.formControl}`} name="tenant_site_id" id="tenant_site_id">
          <option value="">Please Select</option>
          {_.map(tenantsites, opt => (
            <option key={opt.id} value={opt.id}>{opt.name}</option>
          ))}
        </select>
      </div>

      <br className={classes.breakFloat} />
      <Button type="submit" className={classes.submitButton}>Create</Button>
      <Typography variant="h6" component="h2" className={classes.detailsStatus}>
        {status.kind === 'error' ? <span className={classes.errorMessage}>ERROR: {status.message}</span> : status.message}
      </Typography>
    </form>
  )
}

const AtmDeviceAddComponent = ({ add_new_url }) => {
  const [open, setOpen] = useState(false)
  const classes = useStyles()

  return (
    <>
      <Button onClick={() => setOpen(true)}>Add ATM Device</Button>
      <Modal open={open} onClose={() => setOpen(false)}>
        <Box className={classes.detailsBox}>
          <Typography variant="h6" component="h2">Add ATM Device</Typography>
          <hr />
          <AtmDeviceAddForm add_new_url={add_new_url} />
        </Box>
      </Modal>
    </>
  )
}

export default AtmDeviceAddComponent
